/**
 * <s-infinite-scroll> — Loads the next page of a paginated product grid as the
 * buyer nears the end of the list.
 *
 * Progressive enhancement: the server renders the normal numbered pagination
 * plus a "load more" link. Once this element upgrades, the pagination is hidden
 * and the link is observed; when it scrolls near the viewport (or is clicked),
 * the next page is fetched, its grid items are appended, and the link is updated
 * to the following page. Without JavaScript the pagination keeps working.
 *
 * Required child markup:
 *   [data-infinite-grid]        — The list that receives new items (same selector on fetched pages).
 *   a[data-infinite-next]       — Link to the next page; removed when no pages remain.
 *   [data-infinite-pagination]  — Fallback pagination, hidden on upgrade.
 *   [data-infinite-status]      — Polite live region for loading announcements.
 *
 * Element dataset:
 *   data-loading-label, data-loaded-label — Announced text while loading / after loading.
 */
class SInfiniteScroll extends HTMLElement {
  #observer;
  #loading = false;
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const pagination = this.querySelector('[data-infinite-pagination]');
    if (pagination) pagination.hidden = true;
    const next = this.#nextLink();
    if (!next) return;
    next.hidden = false;
    next.addEventListener('click', (event) => {
      event.preventDefault();
      this.#loadNext();
    }, { signal: this.#ac.signal });

    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.#loadNext();
      }, { rootMargin: '0px 0px 800px 0px' });
      this.#observer.observe(next);
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#ac?.abort();
  }

  #nextLink() {
    return this.querySelector('a[data-infinite-next]');
  }

  #announce(text) {
    const status = this.querySelector('[data-infinite-status]');
    if (status && text) status.textContent = text;
  }

  async #loadNext() {
    const next = this.#nextLink();
    if (this.#loading || !next) return;
    this.#loading = true;
    this.setAttribute('aria-busy', 'true');
    next.setAttribute('aria-disabled', 'true');
    this.#announce(this.dataset.loadingLabel);

    try {
      const response = await fetch(next.href, { signal: this.#ac.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const grid = this.querySelector('[data-infinite-grid]');
      const incomingGrid = doc.querySelector('[data-infinite-grid]');
      if (grid && incomingGrid) {
        const items = Array.from(incomingGrid.children);
        items.forEach((item) => {
          item.querySelectorAll('img[loading="eager"]').forEach((img) => img.setAttribute('loading', 'lazy'));
          item.querySelectorAll('img[fetchpriority]').forEach((img) => img.setAttribute('fetchpriority', 'auto'));
          grid.append(document.adoptNode(item));
        });
      }

      const incomingNext = doc.querySelector('s-infinite-scroll a[data-infinite-next]');
      if (incomingNext) {
        next.href = incomingNext.href;
        next.removeAttribute('aria-disabled');
      } else {
        this.#observer?.disconnect();
        next.remove();
      }
      this.#announce(this.dataset.loadedLabel);
    } catch (error) {
      if (error.name === 'AbortError') return;
      // Fall back to normal navigation if the fetch fails.
      window.location.href = next.href;
    } finally {
      this.#loading = false;
      this.removeAttribute('aria-busy');
    }

    // If the link is still in view after appending (short pages), keep going.
    const remaining = this.#nextLink();
    if (remaining && this.#observer) {
      this.#observer.unobserve(remaining);
      this.#observer.observe(remaining);
    }
  }
}

if (!customElements.get('s-infinite-scroll')) {
  customElements.define('s-infinite-scroll', SInfiniteScroll);
}
